import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Permission, UserRole } from '../../types/auth';
import { useAuth } from '../../context/AuthContext';
import { rolePermissions } from '../../types/auth';
import { useEffect, useState, useRef } from 'react';
import LoadingScreen from '../Layout/LoadingScreen';

interface ProtectedRouteProps {
  requiredPermissions?: Permission[];
  children?: React.ReactNode;
}

function hasRequiredPermissions(userRole: UserRole, requiredPermissions: Permission[]): boolean {
  // Convert readonly tuple to regular array
  const userPermissions = [...rolePermissions[userRole]];
  return requiredPermissions.every(permission => userPermissions.includes(permission));
}

export default function ProtectedRoute({ requiredPermissions, children }: ProtectedRouteProps = {}) {
  const { user, isLoading } = useAuth();
  const [showLoading, setShowLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const wasHidden = useRef(false);
  const location = useLocation();

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) {
        wasHidden.current = true;
      } else if (wasHidden.current && user) {
        // If we're becoming visible and we have a user, skip loading
        setShowLoading(false);
        setInitialCheckDone(true);
        wasHidden.current = false;
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Only show loading if we're not returning from a hidden state with a valid session
    if (isLoading && (!wasHidden.current || !user)) {
      setShowLoading(true);
      timeoutId = setTimeout(() => {
        setShowLoading(false);
      }, 500);
    } else {
      setShowLoading(false);
      setInitialCheckDone(true);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, user]);

  // Handle loading state
  if (showLoading || !initialCheckDone) {
    return <LoadingScreen message="Loading..." />;
  }

  // Handle authentication check
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Handle permissions check
  if (requiredPermissions?.length && !hasRequiredPermissions(user.role, requiredPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Render route content
  return children || <Outlet />;
}
